import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import FloatingPhoneButton from "./components/FloatingPhoneButton";
import ChatWidget from "./components/ChatWidget";
import NewHome from "./pages/NewHome";
import { initUTMTracking } from "./utils/utmTracking";
import { trackVisit, trackPageChange } from "./utils/tracking";

// Hide Header/Footer/Widgets on landing page and admin
function ConditionalLayout({ children }) {
  const { pathname } = useLocation();
  const isBarePage = pathname.startsWith('/lp') || pathname.startsWith('/admin');
  return (
    <>
      {!isBarePage && <Header />}
      {children}
      {!isBarePage && <Footer />}
      {!isBarePage && <FloatingPhoneButton />}
      {!isBarePage && <ChatWidget />}
    </>
  );
}

function PageTracker() {
  const location = useLocation();
  const prevPath = React.useRef(null);
  useEffect(() => {
    if (prevPath.current && prevPath.current !== location.pathname) {
      trackPageChange(location.pathname);
    }
    prevPath.current = location.pathname;
  }, [location.pathname]);
  return null;
}

// Lazy-loaded pages for performance
const VerifierEligibilite = lazy(() => import("./pages/VerifierEligibilite"));
const DoucheSenior = lazy(() => import("./pages/DoucheSenior"));
const MonteEscalier = lazy(() => import("./pages/MonteEscalier"));
const AdaptationSalleDeBain = lazy(() => import("./pages/AdaptationSalleDeBain"));
const MaPrimeAdapt = lazy(() => import("./pages/MaPrimeAdapt"));
const RampeAccesHandicap = lazy(() => import("./pages/RampeAccesHandicap"));
const AdaptationLogementParis = lazy(() => import("./pages/AdaptationLogementParis"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const Privacy = lazy(() => import("./pages/Privacy"));
const VoletsRoulants = lazy(() => import("./pages/VoletsRoulants"));
const AmenagementLogementSenior = lazy(() => import("./pages/AmenagementLogementSenior"));
const BarresAppuiSalleBain = lazy(() => import("./pages/BarresAppuiSalleBain"));
const SecurisationEscalier = lazy(() => import("./pages/SecurisationEscalier"));
const AdaptationToilettesHandicap = lazy(() => import("./pages/AdaptationToilettesHandicap"));
const AideAdaptationLogement = lazy(() => import("./pages/AideAdaptationLogement"));
const AideAnahAdaptationLogement = lazy(() => import("./pages/AideAnahAdaptationLogement"));
const FinancementAdaptationLogement = lazy(() => import("./pages/FinancementAdaptationLogement"));
const SubventionMonteEscalier = lazy(() => import("./pages/SubventionMonteEscalier"));
const AideDoucheSenior = lazy(() => import("./pages/AideDoucheSenior"));
const Realisations = lazy(() => import("./pages/Realisations"));
const AidesComplementairesPage = lazy(() => import("./pages/AidesComplementairesPage"));
const AdaptationLogementSenior = lazy(() => import("./pages/AdaptationLogementSenior"));
const AccompagnementAMO = lazy(() => import("./pages/AccompagnementAMO"));
const Contact = lazy(() => import("./pages/Contact"));
const AdaptationLogementSeineEtMarne = lazy(() => import("./pages/AdaptationLogementSeineEtMarne"));
const AdaptationLogementYvelines = lazy(() => import("./pages/AdaptationLogementYvelines"));
const AdaptationLogementEssonne = lazy(() => import("./pages/AdaptationLogementEssonne"));
const AdaptationLogementHautsDeSeine = lazy(() => import("./pages/AdaptationLogementHautsDeSeine"));
const AdaptationLogementSeineSaintDenis = lazy(() => import("./pages/AdaptationLogementSeineSaintDenis"));
const AdaptationLogementValDeMarne = lazy(() => import("./pages/AdaptationLogementValDeMarne"));
const AdaptationLogementValDOise = lazy(() => import("./pages/AdaptationLogementValDOise"));
const AdaptationLogementOise = lazy(() => import("./pages/AdaptationLogementOise"));
const AdaptationLogementAisne = lazy(() => import("./pages/AdaptationLogementAisne"));
const Admin = lazy(() => import("./pages/Admin"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LandingPage2 = lazy(() => import("./pages/LandingPage2"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogMontantMaPrimeAdapt = lazy(() => import("./pages/blog/MontantMaPrimeAdapt"));
const BlogTravauxFinances = lazy(() => import("./pages/blog/TravauxFinances"));
const BlogAdapterSalleDeBain = lazy(() => import("./pages/blog/AdapterSalleDeBain"));
const BlogPrixMonteEscalier = lazy(() => import("./pages/blog/PrixMonteEscalier"));
const BlogAidesAdaptation = lazy(() => import("./pages/blog/AidesAdaptation"));
const DynamicBlogArticle = lazy(() => import("./pages/DynamicBlogArticle"));

function App() {
  useEffect(() => {
    initUTMTracking();
    trackVisit();
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: window.location.pathname
      });
    }
  }, []);

  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <PageTracker />
        <ConditionalLayout>
        <main>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#033B5D] border-t-transparent rounded-full animate-spin"></div></div>}>
          <Routes>
            {/* Landing page Google Ads - pas de navbar */}
            <Route path="/lp" element={<LandingPage />} />
            <Route path="/lp2" element={<LandingPage2 />} />
            {/* Pages principales */}
            <Route path="/" element={<NewHome />} />
            <Route path="/verifier-eligibilite" element={<VerifierEligibilite />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/mentionslegales" element={<MentionsLegales />} />
            <Route path="/privacy" element={<Privacy />} />

            {/* Pages travaux SEO */}
            <Route path="/douche-senior" element={<DoucheSenior />} />
            <Route path="/monte-escalier" element={<MonteEscalier />} />
            <Route path="/adaptation-salle-de-bain" element={<AdaptationSalleDeBain />} />
            <Route path="/rampe-acces-handicap" element={<RampeAccesHandicap />} />
            <Route path="/amenagement-logement-senior" element={<AmenagementLogementSenior />} />
            <Route path="/barres-appui-salle-bain" element={<BarresAppuiSalleBain />} />
            <Route path="/securisation-escalier" element={<SecurisationEscalier />} />
            <Route path="/adaptation-toilettes-handicap" element={<AdaptationToilettesHandicap />} />
            <Route path="/ma-prime-adapt-volets-roulants-electriques-call" element={<VoletsRoulants />} />

            {/* Pages aides financières */}
            <Route path="/ma-prime-adapt" element={<MaPrimeAdapt />} />
            <Route path="/aide-adaptation-logement" element={<AideAdaptationLogement />} />
            <Route path="/aide-anah-adaptation-logement" element={<AideAnahAdaptationLogement />} />
            <Route path="/financement-adaptation-logement" element={<FinancementAdaptationLogement />} />
            <Route path="/subvention-monte-escalier" element={<SubventionMonteEscalier />} />
            <Route path="/aide-douche-senior" element={<AideDoucheSenior />} />
            <Route path="/nos-realisations" element={<Realisations />} />
            <Route path="/aides-complementaires" element={<AidesComplementairesPage />} />

            {/* Pages générales */}
            <Route path="/adaptation-logement-senior" element={<AdaptationLogementSenior />} />
            <Route path="/accompagnement-amo-adaptation-logement" element={<AccompagnementAMO />} />

            {/* Pages SEO locales */}
            <Route path="/adaptation-logement-paris" element={<AdaptationLogementParis />} />
            <Route path="/adaptation-logement-seine-et-marne" element={<AdaptationLogementSeineEtMarne />} />
            <Route path="/adaptation-logement-yvelines" element={<AdaptationLogementYvelines />} />
            <Route path="/adaptation-logement-essonne" element={<AdaptationLogementEssonne />} />
            <Route path="/adaptation-logement-hauts-de-seine" element={<AdaptationLogementHautsDeSeine />} />
            <Route path="/adaptation-logement-seine-saint-denis" element={<AdaptationLogementSeineSaintDenis />} />
            <Route path="/adaptation-logement-val-de-marne" element={<AdaptationLogementValDeMarne />} />
            <Route path="/adaptation-logement-val-doise" element={<AdaptationLogementValDOise />} />
            <Route path="/adaptation-logement-oise" element={<AdaptationLogementOise />} />
            <Route path="/adaptation-logement-aisne" element={<AdaptationLogementAisne />} />

            {/* Admin */}
            <Route path="/admin" element={<Admin />} />

            {/* Blog */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/montant-ma-prime-adapt-2026" element={<BlogMontantMaPrimeAdapt />} />
            <Route path="/blog/travaux-finances-ma-prime-adapt" element={<BlogTravauxFinances />} />
            <Route path="/blog/adapter-salle-de-bain-senior" element={<BlogAdapterSalleDeBain />} />
            <Route path="/blog/prix-monte-escalier" element={<BlogPrixMonteEscalier />} />
            <Route path="/blog/aides-adaptation-logement" element={<BlogAidesAdaptation />} />
            <Route path="/blog/:slug" element={<DynamicBlogArticle />} />
          </Routes>
          </Suspense>
        </main>
        </ConditionalLayout>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
